import type { TransportResponse } from '@mangovoice/api-client'
import type { ExtensionInstance } from '@mangovoice/types'

import { PBX_SETTINGS_ENDPOINTS } from './endpoints'
import type {
  GetCompaniesListParams,
  GetExtensionsParams,
  GetProviderGatewaysV2Params,
  GetServerGroupsV2Params,
  GetSystemSettingsV2Params,
  GetVirtualFaxMachinesParams,
  PaginatedApiResult,
  PbxSettingsApi,
  PbxSettingsApiClient,
  PhoneNumberListItem,
  PhoneNumberListParams,
} from './pbxSettingsApi.types'

const VIRTUAL_FAX_TYPE_FALLBACKS = [
  'ExtensionVirtualFaxMachine',
  'virtual-fax',
  'virtual_fax',
]

const QUERY_LIMIT = 500

const cleanParams = <T extends object>(
  params: T | undefined,
): Record<string, unknown> | undefined => {
  if (!params) return undefined
  const cleaned = Object.entries(params).reduce(
    (acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value
      }
      return acc
    },
    {} as Record<string, unknown>,
  )

  return Object.keys(cleaned).length > 0 ? cleaned : undefined
}

export const createPbxSettingsApi = (
  client: PbxSettingsApiClient,
): PbxSettingsApi => ({
  getPbxV2: (pbxId: number) => {
    return client.get(PBX_SETTINGS_ENDPOINTS.PBX_V2(pbxId))
  },

  patchPbxV2: (pbxId: number, data: Record<string, unknown>) => {
    return client.patch(PBX_SETTINGS_ENDPOINTS.PBX_V2(pbxId), data)
  },

  getProviderGatewaysV2: (params?: GetProviderGatewaysV2Params) => {
    return client.get(
      PBX_SETTINGS_ENDPOINTS.PROVIDER_GATEWAYS_V2,
      cleanParams(params),
    )
  },

  getProviderGatewayV2: (providerGatewayId: number) => {
    return client.get(
      PBX_SETTINGS_ENDPOINTS.PROVIDER_GATEWAY_V2(providerGatewayId),
    )
  },

  getServerGroupsV2: (params?: GetServerGroupsV2Params) => {
    return client.get(
      PBX_SETTINGS_ENDPOINTS.SERVER_GROUPS_V2,
      cleanParams(params),
    )
  },

  getSystemSettingsV2: (params: GetSystemSettingsV2Params) => {
    return client.get(PBX_SETTINGS_ENDPOINTS.SYSTEM_SETTINGS_V2(params.pbx_id))
  },

  patchSystemSettingsV2: (pbxId: number, data: Record<string, unknown>) => {
    return client.patch(PBX_SETTINGS_ENDPOINTS.SYSTEM_SETTINGS_V2(pbxId), data)
  },

  getVirtualFaxMachines: (params: GetVirtualFaxMachinesParams) => {
    return client.get(
      PBX_SETTINGS_ENDPOINTS.VIRTUAL_FAX_MACHINES(params.pbx_id),
    ) as Promise<TransportResponse<PaginatedApiResult<ExtensionInstance>>>
  },

  getVirtualFaxMachinesWithFallback: async (
    params: GetVirtualFaxMachinesParams,
  ) => {
    const primaryResult = await client.get<
      PaginatedApiResult<ExtensionInstance>
    >(PBX_SETTINGS_ENDPOINTS.VIRTUAL_FAX_MACHINES(params.pbx_id))

    if (primaryResult.ok) {
      if ((primaryResult.data?.results ?? []).length > 0) {
        return primaryResult
      }
    }

    for (const extensionType of VIRTUAL_FAX_TYPE_FALLBACKS) {
      const fallbackResult = await client.get<
        PaginatedApiResult<ExtensionInstance>
      >(
        PBX_SETTINGS_ENDPOINTS.EXTENSIONS,
        cleanParams({
          pbx_id: params.pbx_id,
          type: extensionType,
          limit: QUERY_LIMIT,
        }),
      )

      if (fallbackResult.ok && fallbackResult.data!.results.length > 0) {
        return fallbackResult
      }
    }

    if (!primaryResult.ok) {
      return primaryResult
    }

    return primaryResult
  },

  getExtensions: (params: GetExtensionsParams) => {
    return client.get(
      PBX_SETTINGS_ENDPOINTS.EXTENSIONS,
      cleanParams(params),
    ) as Promise<TransportResponse<PaginatedApiResult<ExtensionInstance>>>
  },

  getCompaniesList: (params?: GetCompaniesListParams) => {
    return client.get(PBX_SETTINGS_ENDPOINTS.COMPANIES, cleanParams(params))
  },

  getPhoneNumbers: (params: PhoneNumberListParams) => {
    const { timeout, ...query } = params

    return client.get<PaginatedApiResult<PhoneNumberListItem>>(
      PBX_SETTINGS_ENDPOINTS.PHONE_NUMBERS,
      cleanParams(query),
      timeout ? { timeout } : undefined,
    )
  },
})
